import { Box, Paper } from '@mui/material';
import { t } from 'i18next';
import { BaseController, BaseState } from '../../controllers/BaseController';
import { Severity } from '../../enums/Severity';
import { Details } from '../components/Details';
import { LoadMore } from '../components/LoadMore';
import { SlotsTable } from '../components/SlotsTable';
import { LoadMore as SkeletonLoadMore } from '../components/skeletons/LoadMore';
import { SlotsTable as SkeletonSlotsTable } from '../components/skeletons/SlotsTable';
import { Issue } from '../models/Issue';
import { Slot } from '../models/Slot';
import { SlotRepository } from '../services/SlotRepository';

type Props = {
  issue: Issue;
};

type State = BaseState & {
  initialLoading: boolean;
  loadingData: boolean;
  cursor: string | null;
  hasNextPage: boolean;
  totalItems: number | null;
};

export class IssueDetailsController extends BaseController<Props, State> {
  private readonly repository = new SlotRepository();
  private readonly hideSlots: boolean;
  private data: Slot[];

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.hideSlots = [Severity.FILTERED, Severity.DISABLED, Severity.NA].includes(props.issue.getLevel());

    this.data = [];

    this.state = {
      ...super.getBaseState(),
      initialLoading: !this.hideSlots,
      loadingData: !this.hideSlots,
      cursor: null,
      hasNextPage: !this.hideSlots,
      totalItems: this.hideSlots ? 0 : null,
    };
  }

  componentDidMount() {
    super.componentDidMount();

    // If filtered or N/A severity, don't display slots
    if (!this.hideSlots) {
      this.fetchData('set');
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.repository.cancelAllRequests();
  }

  async fetchData(behavior: 'set' | 'append') {
    try {
      this.setState({ loadingData: true });

      const { data, cursor, hasNextPage, totalItems } = await this.repository.getList(this.props.issue.getId(), this.state.cursor);

      if (behavior === 'set') {
        this.data = data;
      } else {
        this.data.push(...data);
      }

      this.setState({ loadingData: false, initialLoading: false, cursor, hasNextPage, totalItems });
    } catch (e: unknown) {
      // stop loading, but leave the existing data rendered
      this.setState({ loadingData: false, initialLoading: false });
      this.handleError(e, { invalidUrl: t('nonExistingProject') });
    }
  }

  render() {
    const { totalItems } = this.state;
    let loadMoreLabel = t('showMoreInstancesDefault');
    if (totalItems !== null) {
      const remainingItems = totalItems - this.data.length;
      loadMoreLabel = t('showMoreInstances', { count: remainingItems });
    }

    return this.renderBase(
      <Box sx={{ pr: 2, pl: 7, mb: 2 }}>
        <Details model={this.props.issue.getConsequence()} alwaysOpen={this.hideSlots} />

        {this.state.initialLoading ? (
          <>
            <SkeletonSlotsTable />
            <SkeletonLoadMore />
          </>
        ) : (
          this.data.length > 0 && (
            <Paper sx={{ mt: 2 }}>
              <SlotsTable data={this.data} issue={this.props.issue} />
              {this.state.hasNextPage && <LoadMore loading={this.state.loadingData} onLoadMore={() => this.fetchData('append')} label={loadMoreLabel} />}
            </Paper>
          )
        )}
      </Box>
    );
  }
}
